import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Check, Send, Loader2 } from "lucide-react";
import CalendlyButton from "./CalendlyButton";
import { track } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPES = [
  "Cuisine",
  "Dressing",
  "Salle de bain",
  "Mobilier sur-mesure",
  "Agencement commerce",
  "Aménagement bureau",
  "Autre",
];
const BUDGETS = [
  "< 5 000 €",
  "5 000 € – 15 000 €",
  "15 000 € – 30 000 €",
  "30 000 € – 60 000 €",
  "> 60 000 €",
];
const DELAIS = [
  "Dès que possible",
  "Sous 1 mois",
  "1 – 3 mois",
  "3 – 6 mois",
  "Plus de 6 mois",
];

export default function QuoteForm() {
  const [form, setForm] = useState({
    nom: "",
    email: "",
    telephone: "",
    type_projet: "",
    budget: "",
    delai: "",
    message: "",
    website: "", // honeypot — must stay empty
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nom || !form.email || !form.telephone || !form.type_projet || !form.budget || !form.delai) {
      toast.error("Merci de remplir tous les champs requis.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/quotes`, form);
      setSuccess(true);
      toast.success("Demande envoyée ! Je vous recontacte rapidement.");
      // Conversion tracking — fired only on successful submission
      track(
        "generate_lead",
        {
          form_id: "quote_form",
          project_type: form.type_projet,
          budget: form.budget,
          delai: form.delai,
        },
        "Lead",
        { content_name: form.type_projet, content_category: "Quote" }
      );
      setForm({
        nom: "",
        email: "",
        telephone: "",
        type_projet: "",
        budget: "",
        delai: "",
        message: "",
        website: "",
      });
    } catch (err) {
      if (err?.response?.status === 429) {
        toast.error(
          err.response.data?.detail ||
            "Trop de demandes envoyées. Réessayez dans une heure ou contactez-moi directement."
        );
      } else {
        toast.error("Erreur lors de l'envoi. Réessayez ou écrivez-moi directement.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="devis"
      data-testid="quote-section"
      className="bg-stone-50 py-24 sm:py-32"
    >
      <div className="mx-auto max-w-7xl px-6">
        <div className="grid grid-cols-1 gap-12 md:grid-cols-12">
          {/* Left intro */}
          <div className="md:col-span-5">
            <p className="mb-4 text-xs uppercase tracking-[0.25em] text-orange-800">
              Contact
            </p>
            <h2 className="text-3xl font-light leading-tight tracking-tight text-stone-900 sm:text-5xl">
              Parlons de votre projet,
              <br />
              <span className="italic">gratuitement.</span>
            </h2>
            <p className="mt-6 max-w-md text-base text-stone-600">
              Décrivez votre besoin en quelques lignes — agencement, mobilier
              sur mesure, ou les deux. Je reviens vers vous sous 48&nbsp;h
              avec une première proposition adaptée à votre budget.
            </p>

            <div className="mt-10 space-y-4 text-sm text-stone-700">
              {[
                "Étude personnalisée et sans engagement",
                "Mise en concurrence de fabricants sélectionnés",
                "Suivi unique de A à Z, partout sur l'île",
              ].map((it) => (
                <div key={it} className="flex items-start gap-3">
                  <span className="mt-0.5 inline-flex h-5 w-5 items-center justify-center rounded-full bg-orange-700 text-stone-50">
                    <Check className="h-3 w-3" strokeWidth={2} />
                  </span>
                  {it}
                </div>
              ))}
            </div>

            {process.env.REACT_APP_CALENDLY_URL ? (
              <div className="mt-10 rounded-2xl border border-stone-200 bg-white p-6">
                <p className="text-xs uppercase tracking-[0.22em] text-orange-800">
                  Ou plus rapide
                </p>
                <p className="mt-2 text-sm text-stone-600">
                  Préférez-vous échanger de vive voix&nbsp;? Réservez un
                  créneau qui vous convient, je vous appelle.
                </p>
                <div className="mt-4">
                  <CalendlyButton
                    variant="light"
                    testId="quote-section-calendly"
                  />
                </div>
              </div>
            ) : null}
          </div>

          {/* Form card */}
          <div className="md:col-span-7">
            <form
              data-testid="quote-form"
              onSubmit={submit}
              className="rounded-2xl border border-stone-200 bg-white p-6 shadow-sm sm:p-10"
            >
              {success ? (
                <SuccessState onReset={() => setSuccess(false)} />
              ) : (
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  {/* Honeypot — hidden from real users, visible to bots */}
                  <div
                    aria-hidden="true"
                    style={{
                      position: "absolute",
                      left: "-10000px",
                      top: "auto",
                      width: "1px",
                      height: "1px",
                      overflow: "hidden",
                    }}
                  >
                    <label htmlFor="website">Website (laisser vide)</label>
                    <input
                      id="website"
                      type="text"
                      name="website"
                      tabIndex={-1}
                      autoComplete="off"
                      value={form.website}
                      onChange={(e) => update("website", e.target.value)}
                    />
                  </div>

                  <Field label="Nom complet *">
                    <Input
                      data-testid="quote-input-nom"
                      value={form.nom}
                      onChange={(e) => update("nom", e.target.value)}
                      placeholder="Marie Dupont"
                      required
                    />
                  </Field>
                  <Field label="Email *">
                    <Input
                      data-testid="quote-input-email"
                      type="email"
                      value={form.email}
                      onChange={(e) => update("email", e.target.value)}
                      placeholder="marie@email.com"
                      required
                    />
                  </Field>
                  <Field label="Téléphone *">
                    <Input
                      data-testid="quote-input-telephone"
                      value={form.telephone}
                      onChange={(e) => update("telephone", e.target.value)}
                      placeholder="+262 6 92 ..."
                      required
                    />
                  </Field>

                  <Field label="Type de projet *">
                    <Select value={form.type_projet} onValueChange={(v) => update("type_projet", v)}>
                      <SelectTrigger data-testid="quote-select-type">
                        <SelectValue placeholder="Choisir…" />
                      </SelectTrigger>
                      <SelectContent>
                        {TYPES.map((t) => (
                          <SelectItem key={t} value={t}>
                            {t}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>

                  <Field label="Budget estimé *">
                    <Select value={form.budget} onValueChange={(v) => update("budget", v)}>
                      <SelectTrigger data-testid="quote-select-budget">
                        <SelectValue placeholder="Choisir…" />
                      </SelectTrigger>
                      <SelectContent>
                        {BUDGETS.map((b) => (
                          <SelectItem key={b} value={b}>
                            {b}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>

                  <Field label="Délai souhaité *">
                    <Select value={form.delai} onValueChange={(v) => update("delai", v)}>
                      <SelectTrigger data-testid="quote-select-delai">
                        <SelectValue placeholder="Choisir…" />
                      </SelectTrigger>
                      <SelectContent>
                        {DELAIS.map((d) => (
                          <SelectItem key={d} value={d}>
                            {d}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>

                  <div className="sm:col-span-2">
                    <Field label="Votre projet en quelques mots">
                      <Textarea
                        data-testid="quote-input-message"
                        rows={5}
                        value={form.message}
                        onChange={(e) => update("message", e.target.value)}
                        placeholder="Surface, contraintes, style envisagé, photos d'inspiration…"
                      />
                    </Field>
                  </div>

                  <div className="sm:col-span-2 flex items-center justify-between gap-4 pt-2">
                    <p className="text-xs text-stone-500">
                      En envoyant, vous acceptez d'être recontacté(e) par SAD.
                    </p>
                    <Button
                      data-testid="quote-submit-button"
                      type="submit"
                      disabled={loading}
                      className="h-12 rounded-full bg-stone-900 px-6 text-stone-50 hover:bg-orange-800"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" strokeWidth={1.5} />
                          Envoi…
                        </>
                      ) : (
                        <>
                          <Send className="mr-2 h-4 w-4" strokeWidth={1.5} />
                          Créer votre espace sur mesure
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

const Field = ({ label, children }) => (
  <div>
    <Label className="mb-2 block text-xs uppercase tracking-[0.18em] text-stone-500">
      {label}
    </Label>
    {children}
  </div>
);

const SuccessState = ({ onReset }) => (
  <div data-testid="quote-success" className="py-12 text-center">
    <div className="mx-auto mb-6 inline-flex h-14 w-14 items-center justify-center rounded-full bg-orange-700 text-stone-50">
      <Check className="h-6 w-6" strokeWidth={1.5} />
    </div>
    <h3 className="text-2xl font-light tracking-tight">Merci !</h3>
    <p className="mx-auto mt-3 max-w-md text-stone-600">
      Votre demande a bien été envoyée. Je vous recontacte sous 48&nbsp;h pour
      en discuter en détail.
    </p>
    {process.env.REACT_APP_CALENDLY_URL ? (
      <p className="mx-auto mt-2 max-w-md text-sm text-stone-500">
        Envie d'avancer plus vite&nbsp;? Réservez directement un créneau
        d'appel ci-dessous.
      </p>
    ) : null}
    <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
      <CalendlyButton
        variant="light"
        testId="quote-success-calendly"
      />
      <Button
        onClick={onReset}
        variant="outline"
        className="rounded-full"
        data-testid="quote-reset"
      >
        Envoyer une autre demande
      </Button>
    </div>
  </div>
);
